import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { UseQueryResult } from '@tanstack/react-query';
import { PopUpProductDetails, ProductsSequenceProduct } from 'common/contracts';
import { EToasterStatus, showToast } from 'utils/showToast';
import { v4 as uuidv4 } from 'uuid';
import { ProductEntry } from 'views/Offers/Popups/Form/hooks/usePopupProducts';

import Button from 'components/Button/Button';
import DialogModal from 'components/DialogModal/DialogModal';
import RichText from 'components/RichText/RichText';

import {
  buildEmptyWeek,
  REWARD_CALENDAR_ADD_WEEK_REQUIRES_DAYS_MESSAGE,
  REWARD_CALENDAR_DAYS_PER_WEEK,
  REWARD_CALENDAR_MAX_WEEKS,
  REWARD_CALENDAR_MIN_WEEKS,
  RewardCalendarFormFields
} from '../../RewardCalendarForm.consts';
import { resolveActiveWeekIndexAfterWeekRemoved } from '../../RewardCalendarForm.utils';
import { DayCard } from '../DayCard';
import { DayProductsList } from '../DayProductsList';
import { WeekChip } from '../WeekChip';

import './ConfigurationTab.css';

const quantityOf = (p: ProductEntry): number => Number(p.quantity) || 0;

const buildDraftEntry = (): ProductEntry => ({ id: uuidv4() } as ProductEntry);

const isDraftComplete = (entry: ProductEntry | null | undefined): boolean =>
  !!entry?.productId && (Number(entry.quantity) || 0) > 0;

interface ScrollableCardBodyProps {
  className: string;
  children: React.ReactNode;
}

const ScrollableCardBody: FC<ScrollableCardBodyProps> = ({ className, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const header = el.parentElement?.querySelector('[data-day-card-header]');
    const update = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight;
      el.toggleAttribute('data-has-overflow', hasOverflow);
      if (header) header.toggleAttribute('data-scrolled', el.scrollTop > 0);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  return (
    <div data-day-card-scroll className={className} ref={ref}>
      {children}
    </div>
  );
};

function giftIconCountedRewardRows(products: ProductEntry[]): number {
  return products.filter((p) => !!p.productId).length;
}

function dayHasFilledReward(products: ProductEntry[]): boolean {
  return products.some((p) => !!p.productId && quantityOf(p) > 0);
}

interface IConfigurationTabProps {
  getProducts: UseQueryResult;
  activeWeekIndex: number;
  onActiveWeekChange: (index: number) => void;
  isActive: boolean;
}

const ConfigurationTab: FC<IConfigurationTabProps> = ({
  getProducts,
  activeWeekIndex,
  onActiveWeekChange,
  isActive
}) => {
  const form = useFormContext<RewardCalendarFormFields>();
  const weeks = form.watch('weeks');

  const totalAvailableProducts = useMemo(() => {
    const data = getProducts.data as
      | { result: PopUpProductDetails[] }
      | undefined;
    return data?.result.length ?? 0;
  }, [getProducts.data]);

  const [openDayIndex, setOpenDayIndex] = useState<number | null>(null);
  const [draftEntries, setDraftEntries] = useState<Record<string, ProductEntry>>({});

  useEffect(() => {
    if (!isActive) {
      setOpenDayIndex(null);
      setDraftEntries({});
    }
  }, [isActive]);

  const [weekIndexToDelete, setWeekIndexToDelete] = useState<number | null>(
    null
  );

  const canAddWeek = weeks.length < REWARD_CALENDAR_MAX_WEEKS;
  const canRemoveWeek = weeks.length > REWARD_CALENDAR_MIN_WEEKS;

  const activeWeek = weeks[activeWeekIndex] ?? weeks[0];
  const activeWeekDays = activeWeek?.days ?? [];

  const writeWeeks = useCallback(
    (nextWeeks: RewardCalendarFormFields['weeks']): void => {
      form.setValue('weeks', nextWeeks, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const handleAddWeek = useCallback((): void => {
    if (!canAddWeek) return;
    const weekToValidate = weeks[weeks.length - 1];
    const everyDayHasProduct = weekToValidate.days.every((day) =>
      dayHasFilledReward(day.products)
    );
    if (!everyDayHasProduct) {
      showToast({
        message: REWARD_CALENDAR_ADD_WEEK_REQUIRES_DAYS_MESSAGE,
        status: EToasterStatus.ERROR
      });
      return;
    }
    const next = [...weeks, buildEmptyWeek()];
    writeWeeks(next);
    onActiveWeekChange(next.length - 1);
  }, [canAddWeek, weeks, writeWeeks, onActiveWeekChange]);

  const handleRemoveWeek = useCallback(
    (weekIndex: number): void => {
      if (weeks.length <= REWARD_CALENDAR_MIN_WEEKS) return;
      const next = weeks.filter((_, i) => i !== weekIndex);
      writeWeeks(next);
      onActiveWeekChange(
        resolveActiveWeekIndexAfterWeekRemoved(
          activeWeekIndex,
          weekIndex,
          next.length
        )
      );
      setOpenDayIndex(null);
    },
    [weeks, writeWeeks, activeWeekIndex, onActiveWeekChange]
  );

  const mutateDayProducts = useCallback(
    (
      weekIndex: number,
      dayIndex: number,
      mutator: (products: ProductEntry[]) => ProductEntry[]
    ): RewardCalendarFormFields['weeks'] => {
      const currentWeeks = form.getValues('weeks');
      const next = currentWeeks.map((week, wi) => {
        if (wi !== weekIndex) return week;
        return {
          ...week,
          days: week.days.map((day, di) => {
            if (di !== dayIndex) return day;
            return { ...day, products: mutator(day.products) };
          })
        };
      });
      writeWeeks(next);
      return next;
    },
    [form, writeWeeks]
  );

  const buildDayCell = useCallback(
    (
      weekIndex: number,
      dayIndex: number,
      products: ProductEntry[],
      draft: ProductEntry | undefined
    ) => {
      const draftKey = `${weekIndex}-${dayIndex}`;

      const openDraft = (): void => {
        setDraftEntries((prev) => ({
          ...prev,
          [draftKey]: prev[draftKey] ?? buildDraftEntry()
        }));
      };

      const closeDraft = (): void => {
        setDraftEntries((prev) => {
          const next = { ...prev };
          delete next[draftKey];
          return next;
        });
      };

      const handleAddProduct = (): void => {
        if (isDraftComplete(draft)) {
          mutateDayProducts(weekIndex, dayIndex, (list) => [
            ...list,
            { ...draft, id: draft!.id }
          ]);
          setDraftEntries((prev) => ({ ...prev, [draftKey]: buildDraftEntry() }));
        }
      };

      const continueToNextDay = (): void => {
        const nextIndex = dayIndex + 1;
        const nextDraftKey = `${weekIndex}-${nextIndex}`;
        setDraftEntries((prev) => {
          const next = { ...prev };
          delete next[draftKey];
          next[nextDraftKey] = next[nextDraftKey] ?? buildDraftEntry();
          return next;
        });
        setOpenDayIndex(nextIndex);
      };

      const onDraftChange = (entry: ProductEntry): void => {
        setDraftEntries((prev) => ({ ...prev, [draftKey]: entry }));
      };

      const committedAndDraftCount =
        products.filter((p) => p.productId).length + (draft?.productId ? 1 : 0);

      return {
        handlers: {
          updateProduct: (id: string, product: ProductsSequenceProduct) =>
            mutateDayProducts(weekIndex, dayIndex, (list) =>
              list.map((p) => (p.id === id ? { ...p, ...product } : p))
            ),
          deleteProduct: (id: string) =>
            mutateDayProducts(weekIndex, dayIndex, (list) =>
              list.filter((p) => p.id !== id)
            )
        },
        rewardQuantityTotal: giftIconCountedRewardRows(products),
        canAddNext: isDraftComplete(draft),
        hasFilledReward: dayHasFilledReward(products) || isDraftComplete(draft),
        committedAndDraftCount,
        openDraft,
        closeDraft,
        onDraftChange,
        handleAddProduct,
        continueToNextDay
      };
    },
    [mutateDayProducts]
  );

  const weekChips = useMemo(
    () =>
      weeks.map((week, index) => (
        <WeekChip
          key={week.id}
          label={`Week ${index + 1}`}
          isActive={index === activeWeekIndex}
          canRemove={canRemoveWeek}
          onSelect={() => {
            onActiveWeekChange(index);
            setOpenDayIndex(null);
          }}
          onRemove={() => setWeekIndexToDelete(index)}
        />
      )),
    [weeks, activeWeekIndex, canRemoveWeek, onActiveWeekChange]
  );

  return (
    <div className="flex flex-col">
      <RichText
        title="Configuration"
        description="Set up the reward calendar using weeks. Each week contains 7 days where rewards can be configured."
        className="mb-5"
      />

      <div className="flex flex-wrap items-center gap-2 py-5 border-y border-base-sidebarBorder">
        {weekChips}
        {canAddWeek && (
          <button
            type="button"
            onClick={handleAddWeek}
            className="inline-flex items-center justify-center gap-2 flex-shrink-0 w-[83px] h-8 py-2 px-3 rounded-md border border-dashed border-zinc-200 bg-white text-p-xs font-medium transition-colors text-base-foreground hover:bg-white hover:text-indigo-600 hover:border-indigo-600"
          >
            <FontAwesomeIcon
              icon="fa-regular fa-plus"
              className="size-3 shrink-0 text-p-xs font-semibold leading-none text-current"
            />
            <span>Week</span>
          </button>
        )}
      </div>

      {activeWeek && (
        <div className="mt-6">
          <h4 className="text-p-m font-semibold text-base-foreground mb-4">
            Week {activeWeekIndex + 1}
          </h4>

          <div className="grid grid-cols-4 gap-3">
            {activeWeekDays.map((day, dayIndex) => {
              const draftEntry = draftEntries[`${activeWeekIndex}-${dayIndex}`];

              const {
                handlers,
                rewardQuantityTotal,
                canAddNext,
                hasFilledReward,
                committedAndDraftCount,
                openDraft,
                closeDraft,
                onDraftChange,
                handleAddProduct,
                continueToNextDay
              } = buildDayCell(
                activeWeekIndex,
                dayIndex,
                day.products,
                draftEntry
              );

              return (
                <DayCard
                  key={day.id}
                  dayLabel={`Day ${dayIndex + 1}`}
                  rewardCount={rewardQuantityTotal}
                  isOpen={openDayIndex === dayIndex}
                  onOpenChange={(open) => {
                    setOpenDayIndex(open ? dayIndex : null);
                    if (open) openDraft();
                    else closeDraft();
                  }}
                  onClose={() => {
                    setOpenDayIndex(null);
                    closeDraft();
                  }}
                  footer={
                    <div className="flex flex-col gap-2 w-full items-stretch">
                      <Button
                        type="button"
                        variant="secondary"
                        className="self-stretch"
                        disabled={
                          !canAddNext ||
                          committedAndDraftCount >= totalAvailableProducts
                        }
                        leftIcon={
                          <FontAwesomeIcon icon="fa-regular fa-circle-plus" />
                        }
                        onClick={handleAddProduct}
                      >
                        Add Product
                      </Button>
                      {hasFilledReward &&
                        dayIndex < REWARD_CALENDAR_DAYS_PER_WEEK - 1 && (
                          <Button
                            type="button"
                            variant="default"
                            className="flex h-10 shrink-0 items-center justify-center gap-2 self-stretch rounded-md px-4 py-2 bg-indigo-700 text-white hover:bg-indigo-800"
                            onClick={continueToNextDay}
                          >
                            Continue to the Next Day
                          </Button>
                        )}
                    </div>
                  }
                >
                  <div className="mb-4 flex shrink-0 flex-col gap-[18px]" data-day-card-header>
                    <div className="flex w-full shrink-0 items-center gap-2 pr-12 text-p-m">
                      <span className="font-semibold text-base-foreground">
                        Day {dayIndex + 1}
                      </span>
                      <span className="text-zinc-200" aria-hidden>
                        |
                      </span>
                      <span className="text-base-mutedForeground">
                        Week {activeWeekIndex + 1}
                      </span>
                    </div>
                    <div className="flex shrink-0 w-full items-center justify-between">
                      <span className="text-p-s font-semibold text-base-foreground">
                        Rewards
                      </span>
                      <span className="inline-flex items-center gap-1 text-p-s text-[#A1A1AA]">
                        <FontAwesomeIcon
                          icon="fa-regular fa-gift"
                          className="text-sm leading-none"
                        />
                        <span>{rewardQuantityTotal}</span>
                      </span>
                    </div>
                  </div>
                  <ScrollableCardBody
                    className="flex-1 max-h-[min(320px,calc(100dvh-13rem))] min-h-[38px] min-w-0 w-full overflow-y-auto overscroll-contain"
                  >
                    <DayProductsList
                      products={getProducts}
                      productsList={day.products}
                      updateProduct={handlers.updateProduct}
                      deleteProduct={handlers.deleteProduct}
                      draftEntry={draftEntry}
                      onDraftChange={onDraftChange}
                      onDraftDiscard={closeDraft}
                    />
                  </ScrollableCardBody>
                </DayCard>
              );
            })}
          </div>
        </div>
      )}

      {weekIndexToDelete !== null && (
        <DialogModal
          isOpen
          dataTestId="RewardCalendar-DeleteWeekModal"
          title={`Delete Week ${weekIndexToDelete + 1}`}
          customDescriptionContent={
            <>
              <span className="block">
                Are you sure you want to delete this week?
              </span>
              <span className="block mt-1">
                This action can&apos;t be undone.
              </span>
            </>
          }
          modalActions={{
            onConfirm: {
              label: 'Delete',
              action: () => {
                handleRemoveWeek(weekIndexToDelete);
                setWeekIndexToDelete(null);
              }
            },
            onClose: {
              label: 'Cancel',
              action: () => setWeekIndexToDelete(null),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default ConfigurationTab;
